import { doc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { pairNextSwissRound } from '@/engine';
import { matchesCol } from './matches';
import { participantsCol } from './participants';
import type { Match, Participant, Tournament } from './types';

export async function generateNextSwissRound(tournament: Tournament): Promise<void> {
  if (tournament.format !== 'SWISS') throw new Error('Не Swiss');
  if (tournament.status !== 'IN_PROGRESS') throw new Error('Турнир не в процессе');

  const matchesSnap = await getDocs(matchesCol(tournament.id));
  const history: Match[] = matchesSnap.docs.map((d) => d.data());

  const lastRound = history.length > 0 ? Math.max(...history.map((m) => m.round)) : 0;
  const incomplete = history.filter((m) => m.status !== 'COMPLETED');
  if (incomplete.length > 0) {
    throw new Error('Сначала доиграйте текущий тур');
  }
  if (lastRound >= (tournament.swissRounds ?? 0)) {
    throw new Error('Все туры уже сыграны');
  }

  const nextRound = lastRound + 1;
  // Защита от двойной генерации: матчи с детерминированными id перезаписались бы молча.
  if (history.some((m) => m.round === nextRound)) {
    throw new Error(`Тур ${nextRound} уже сгенерирован`);
  }

  const participantsSnap = await getDocs(participantsCol(tournament.id));
  const participants: Participant[] = participantsSnap.docs.map((d) => d.data());

  const next = pairNextSwissRound(
    participants.map((p) => ({ id: p.id })),
    history,
    nextRound
  );

  const participantById = new Map(participants.map((p) => [p.id, p]));
  const batch = writeBatch(db);
  for (const g of next.matches) {
    const ref = doc(db, 'tournaments', tournament.id, 'matches', g.id);
    const aName = g.playerAId ? participantById.get(g.playerAId)?.displayName ?? null : null;
    const bName = g.playerBId ? participantById.get(g.playerBId)?.displayName ?? null : null;
    const m: Match = {
      id: g.id,
      stage: 'GROUP',
      round: g.round,
      slot: g.slot,
      playerAId: g.playerAId,
      playerBId: g.playerBId,
      playerAName: aName,
      playerBName: bName,
      status: g.autoAdvanceWinnerId ? 'COMPLETED' : 'READY',
      ...(g.autoAdvanceWinnerId
        ? {
            score: { sets: [], winnerId: g.autoAdvanceWinnerId, summary: 'bye' },
          }
        : {}),
    };
    batch.set(ref, m);
  }
  batch.update(doc(db, 'tournaments', tournament.id), {
    updatedAt: serverTimestamp(),
  });
  // Если только что сгенерировали последний тур, статус не меняем (он станет COMPLETED после последнего матча)
  await batch.commit();
}
